// Server-side authentication service with JWT tokens and rate limiting
interface AccountWithStatus {
  name: string;
  status: string;
}

interface ServerValidationResponse {
  valid: boolean;
  accounts?: string[];
  accounts_with_status?: AccountWithStatus[];
  access_token?: string;
  expires_in?: number;
  error?: string;
}

interface StoredAuth {
  access_token: string;
  email: string;
  accounts: string[];
  expires_at: number;
}

const AUTH_STORAGE_KEY = 'crm_auth_token';
const VALIDATION_ENDPOINT = `${import.meta.env.VITE_AIRTABLE_PROXY_URL}/validate-user`;

export class AuthService {
  private static instance: AuthService;
  
  private constructor() {}
  
  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  /**
   * Validate user credentials server-side with rate limiting and JWT tokens
   */
  async validateUserServerSide(email: string, isLoginAttempt: boolean = false, userId?: string, sessionId?: string): Promise<ServerValidationResponse> {
    try {
      const response = await fetch(VALIDATION_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email, 
          is_login_attempt: isLoginAttempt,
          user_id: userId || '',
          session_id: sessionId || ''
        }),
      });

      const data = await response.json();

      if (response.ok && data.valid) {
        // Store authentication data securely
        this.storeAuthData({
          access_token: data.access_token,
          email,
          accounts: data.accounts,
          expires_at: Date.now() + (data.expires_in * 1000)
        });

        // Verbose debug log - commented for production
        // console.log('Server-side validation successful');
        return data;
      } else {
        console.warn('Server-side validation failed:', data.error);
        return data;
      }
    } catch (error) {
      console.error('Server-side validation error:', error);
      return {
        valid: false,
        error: 'Authentication service unavailable. Please try again.'
      };
    }
  }

  /**
   * Check if user has valid authentication
   */
  isAuthenticated(): boolean {
    const authData = this.getStoredAuthData();
    if (!authData) {
      return false;
    }

    // Check if token is expired
    if (Date.now() >= authData.expires_at) {
      this.clearAuthData();
      return false;
    }

    return true;
  }

  /**
   * Get stored authentication data
   */
  getStoredAuthData(): StoredAuth | null {
    try {
      const stored = localStorage.getItem(AUTH_STORAGE_KEY);
      if (!stored) {
        return null;
      }
      
      const authData = JSON.parse(stored);
      
      // Validate stored data structure
      if (!authData.access_token || !authData.email || !authData.accounts || !authData.expires_at) {
        this.clearAuthData();
        return null;
      }
      
      return authData;
    } catch (error) {
      console.error('Error reading stored auth data:', error);
      this.clearAuthData();
      return null;
    }
  }

  /**
   * Get user's available accounts
   */
  getUserAccounts(): string[] {
    const authData = this.getStoredAuthData();
    return authData?.accounts || [];
  }

  /**
   * Get user's email
   */
  getUserEmail(): string | null {
    const authData = this.getStoredAuthData();
    return authData?.email || null;
  }

  /**
   * Get access token for API requests
   */
  getAccessToken(): string | null {
    const authData = this.getStoredAuthData();
    if (!authData || Date.now() >= authData.expires_at) {
      return null;
    }
    return authData.access_token;
  }

  /**
   * Clear authentication data (logout)
   */
  clearAuthData(): void {
    localStorage.removeItem(AUTH_STORAGE_KEY);
  }

  /**
   * Store authentication data securely
   */
  private storeAuthData(authData: StoredAuth): void {
    try {
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(authData));
    } catch (error) {
      console.error('Error storing auth data:', error);
    }
  }

  /**
   * Refresh authentication if needed
   */
  async refreshAuthIfNeeded(userId?: string, sessionId?: string): Promise<boolean> {
    const authData = this.getStoredAuthData();
    if (!authData) {
      return false;
    }

    // Refresh if token expires within 1 hour
    const oneHour = 60 * 60 * 1000;
    if (Date.now() + oneHour >= authData.expires_at) {
      const result = await this.validateUserServerSide(authData.email, false, userId, sessionId); // Not a login attempt
      return result.valid;
    }

    return true;
  }
}

// Export singleton instance
export const authService = AuthService.getInstance();